import { createFileRoute, redirect } from "@tanstack/react-router";
import { Button } from "@provenleap/ui";
import { ClaudeMark, OpenAIMark } from "../../../components/ai-access/BrandIcons";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { MCPServerCard } from "../../../components/ai-access/MCPServerCard";
import { AIAssistantCard, type Assistant } from "../../../components/ai-access/AIAssistantCard";
import { Toast } from "../../../components/clients/Toast";
import { useToast } from "../../../hooks/useToast";
import { useAuth } from "../../../stores/authStore";
import { mcpConnectUiEnabled } from "../../../lib/mcp/connect-flag";

export const Route = createFileRoute("/_authenticated/settings/ai-access")({
  // Story 29-17 interlock: the MCP connect surface stays DARK until the master
  // switch is flipped on. With the flag off, the route is unreachable (even by
  // direct URL) — redirect to Settings.
  beforeLoad: () => {
    if (!mcpConnectUiEnabled()) {
      throw redirect({ to: "/settings" });
    }
  },
  component: AIAccessPage,
});

const assistants: Assistant[] = [
  {
    name: "Claude",
    icon: ClaudeMark,
    description: "Anthropic's AI assistant with native MCP support",
    connectUrl: "https://claude.ai/customize/connectors",
    setupSteps: [
      "Click 'Add custom connector' on the page that just opened",
      "Paste the MCP Server URL (already in your clipboard)",
      "Click 'Add', then 'Connect' to sign in to ProvenLeap",
      "Authorize access to your data",
    ],
  },
  {
    name: "ChatGPT",
    icon: OpenAIMark,
    description: "OpenAI's assistant with MCP support via Connectors",
    connectUrl: "https://chatgpt.com/#settings/Connectors",
    setupSteps: [
      "Click 'Add' or 'Add custom connector' on the page that just opened",
      "Paste the MCP Server URL (already in your clipboard)",
      "Sign in to ProvenLeap when prompted to authorize",
    ],
  },
];

function buildMcpServerUrl(_userId: string): string {
  // All users connect to the SAME endpoint; the OAuth consent flow identifies the
  // user + tenant from their session. There is no per-user path on the worker
  // (a `/u/:id` URL 404s) — the MCP Streamable-HTTP endpoint is `/mcp`.
  return "https://mcp.provenleap.com/mcp";
}

function AIAccessPage() {
  const auth = useAuth();
  const { toast, showToast } = useToast(6000);

  const isLoading = auth.isLoading;
  const userId = auth.user?.id;
  const mcpServerUrl = userId ? buildMcpServerUrl(userId) : null;

  return (
    <div className="p-6 max-w-[800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "AI Access" }]} />

      {/* Connect to AI assistants — the primary action, first on the page */}
      <section className="mt-2 mb-8">
        <h2 className="text-[15px] font-semibold text-foreground mb-3">
          Connect to AI assistants
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {isLoading || !mcpServerUrl
            ? assistants.map((a) => <AssistantCardSkeleton key={a.name} />)
            : assistants.map((a) => (
                <AIAssistantCard
                  key={a.name}
                  assistant={a}
                  mcpServerUrl={mcpServerUrl}
                  disabled={!mcpServerUrl}
                  showToast={showToast}
                />
              ))}
        </div>
      </section>

      {/* MCP Server URL — for manual / advanced connection */}
      <section>
        {isLoading ? (
          <MCPServerCardSkeleton />
        ) : mcpServerUrl ? (
          <MCPServerCard url={mcpServerUrl} showToast={showToast} />
        ) : (
          <MCPServerNotProvisioned showToast={showToast} />
        )}
      </section>

      <Toast toast={toast} />

      <p className="mt-10 text-[11px] leading-relaxed text-muted-foreground/70">
        Claude is a trademark of Anthropic PBC. ChatGPT is a trademark of OpenAI, Inc.
        ProvenLeap is an independent integration using the Model Context Protocol and is
        not affiliated with, sponsored by, or endorsed by Anthropic or OpenAI.
      </p>
    </div>
  );
}

function MCPServerCardSkeleton() {
  return (
    <div
      className="rounded-[22px] bg-background p-6 shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
      aria-busy="true"
      aria-label="Loading MCP server"
    >
      <div className="h-3 w-32 rounded bg-muted/60 animate-pulse" />
      <div className="mt-3 flex gap-2">
        <div className="flex-1 h-10 rounded-2xl bg-muted/60 animate-pulse" />
        <div className="h-10 w-20 rounded-2xl bg-muted/60 animate-pulse" />
      </div>
      <div className="mt-3 h-3 w-2/3 rounded bg-muted/40 animate-pulse" />
    </div>
  );
}

function AssistantCardSkeleton() {
  return (
    <div
      className="rounded-[22px] bg-background p-6 shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-muted/60 animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-24 rounded bg-muted/60 animate-pulse" />
          <div className="h-3 w-full rounded bg-muted/40 animate-pulse" />
        </div>
      </div>
      <div className="mt-5 h-10 w-full rounded-2xl bg-muted/60 animate-pulse" />
    </div>
  );
}

function MCPServerNotProvisioned({
  showToast,
}: {
  showToast: (type: "success" | "error" | "info", message: string) => void;
}) {
  return (
    <div
      className="rounded-[22px] bg-background p-8 shadow-xs text-center"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <h3 className="text-[15px] font-semibold text-foreground">
        Your MCP server isn't ready yet
      </h3>
      <p className="text-[13px] text-muted-foreground mt-1 mb-4">
        Provision your unique connection URL to start linking AI assistants.
      </p>
      <Button
        variant="brand"
        className="rounded-2xl text-[13px] cursor-pointer"
        onClick={() =>
          showToast(
            "info",
            "Provisioning isn't wired up yet — check back soon.",
          )
        }
        data-testid="provision-mcp-server"
      >
        Provision MCP server
      </Button>
    </div>
  );
}
